import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sun, Mail } from "lucide-react";
import LanguageSwitcher from "../components/LanguageSwitcher";

/* Legal — shared shell for Privacy & Terms pages. Two routes ( /privacy and
   /voorwaarden ) render this component with a `type` prop. Content is kept
   intentionally short and modular; line items are easy to edit when legal
   counsel reviews the final version. */

const STR = {
  nl: {
    nav_back: "Terug naar home", nav_calc: "Naar de calculator",
    last_updated: "Laatst bijgewerkt: februari 2026",
    review_note: "Versie te beoordelen door juridisch adviseur — verwerking conform GDPR (AVG) en Spaanse LOPDGDD.",
    contact_h: "Vragen?",
    contact_p: "Voor privacy- of juridische vragen mail naar",
    privacy: {
      title: "Privacybeleid",
      sub: "Hoe Recywatt Iberica S.L. omgaat met jouw gegevens.",
      blocks: [
        { h: "1. Wie zijn wij?",
          p: "Verantwoordelijke voor de verwerking is Recywatt Iberica S.L., met statutaire zetel in Spanje. Wij exploiteren het platform DoctorSol via de domeinen doctorsol.es en gerelateerde subdomeinen." },
        { h: "2. Welke gegevens verzamelen wij?",
          p: "Wij verwerken: naam, e-mailadres, telefoonnummer/WhatsApp, locatie (stad/postcode), gegevens uit je elektriciteitsfactuur (indien geüpload), antwoorden op de calculator-vragen, IP-adres en browsergegevens." },
        { h: "3. Waarvoor gebruiken wij ze?",
          p: "Voor het uitvoeren van de berekening, het opstellen van je persoonlijke rapport of offerte, het verzenden van bevestigingsmails, fraudepreventie en geanonimiseerde marktanalyse." },
        { h: "4. Met wie delen wij?",
          p: "Alleen met geselecteerde dienstverleners die nodig zijn voor het leveren van onze service (e-mail-aanbieder, hosting, kaartdiensten, betalingsverwerking). Nooit met derden voor marketing zonder jouw expliciete toestemming." },
        { h: "5. Hoe lang bewaren wij?",
          p: "Lead- en intake-gegevens 24 maanden na laatste interactie. Je kunt eerder om verwijdering verzoeken via onderstaand contactadres." },
        { h: "6. Jouw rechten",
          p: "Recht op inzage, correctie, verwijdering, beperking en overdraagbaarheid van jouw gegevens. Klachten kun je indienen bij de Spaanse toezichthouder AEPD (aepd.es)." },
        { h: "7. Cookies & tracking",
          p: "Wij gebruiken essentiële cookies plus, met jouw toestemming, Meta Pixel en Google Analytics 4 om de werking van onze calculator te verbeteren. Je kunt deze altijd weigeren via je browser-instellingen." },
      ],
    },
    terms: {
      title: "Algemene voorwaarden",
      sub: "Voorwaarden bij het gebruik van DoctorSol.",
      blocks: [
        { h: "1. Dienst",
          p: "DoctorSol biedt een gratis online calculator die jouw potentiële besparing op zonne-energie inschat op basis van de door jou verstrekte gegevens. De resultaten zijn een schatting en geen bindende offerte." },
        { h: "2. Geen verplichting",
          p: "Het gebruik van de calculator en het ontvangen van een rapport of offerte verplicht je tot niets. Je kunt op elk moment stoppen en je gegevens laten verwijderen." },
        { h: "3. Nauwkeurigheid",
          p: "Onze berekeningen zijn gebaseerd op gemiddelde Spaanse marktdata. De daadwerkelijke besparing kan afwijken door specifieke omstandigheden van jouw woning, tariefwijzigingen of regionale subsidies." },
        { h: "4. Aansprakelijkheid",
          p: "Recywatt Iberica S.L. is niet aansprakelijk voor beslissingen die je neemt op basis van de calculator. Voor een installatie raden wij altijd een gecertificeerde installateur aan." },
        { h: "5. Intellectueel eigendom",
          p: "Alle content, software en visuele elementen op DoctorSol zijn eigendom van Recywatt Iberica S.L. en mogen niet zonder schriftelijke toestemming worden gekopieerd." },
        { h: "6. Toepasselijk recht",
          p: "Op deze voorwaarden is Spaans recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in de plaats van vestiging van Recywatt Iberica S.L." },
      ],
    },
  },
  es: {
    nav_back: "Volver al inicio", nav_calc: "Ir a la calculadora",
    last_updated: "Última actualización: febrero 2026",
    review_note: "Versión sujeta a revisión legal — tratamiento conforme al GDPR y la LOPDGDD española.",
    contact_h: "¿Preguntas?",
    contact_p: "Para cuestiones de privacidad o legales escríbenos a",
    privacy: {
      title: "Política de Privacidad",
      sub: "Cómo Recywatt Iberica S.L. gestiona tus datos.",
      blocks: [
        { h: "1. ¿Quiénes somos?", p: "El responsable del tratamiento es Recywatt Iberica S.L., con domicilio social en España. Operamos la plataforma DoctorSol a través de los dominios doctorsol.es y subdominios relacionados." },
        { h: "2. ¿Qué datos recogemos?", p: "Tratamos: nombre, email, teléfono/WhatsApp, ubicación (ciudad/código postal), datos de tu factura eléctrica (si la subes), respuestas a las preguntas de la calculadora, dirección IP y datos de navegador." },
        { h: "3. ¿Para qué los usamos?", p: "Para realizar el cálculo, generar tu informe o presupuesto personal, enviar emails de confirmación, prevención de fraude y análisis anonimizado del mercado." },
        { h: "4. ¿Con quién compartimos?", p: "Solo con proveedores necesarios para prestar el servicio (email, hosting, mapas, procesamiento de pagos). Nunca con terceros con fines comerciales sin tu permiso explícito." },
        { h: "5. ¿Cuánto tiempo guardamos?", p: "Los datos de lead e intake durante 24 meses tras la última interacción. Puedes solicitar la eliminación antes a través del contacto inferior." },
        { h: "6. Tus derechos", p: "Derecho de acceso, rectificación, supresión, limitación y portabilidad de tus datos. Puedes reclamar ante la AEPD (aepd.es)." },
        { h: "7. Cookies & analítica", p: "Usamos cookies esenciales y, con tu consentimiento, Meta Pixel y Google Analytics 4 para mejorar la calculadora. Puedes rechazarlas en cualquier momento desde tu navegador." },
      ],
    },
    terms: {
      title: "Términos y Condiciones",
      sub: "Condiciones de uso de DoctorSol.",
      blocks: [
        { h: "1. Servicio", p: "DoctorSol ofrece una calculadora online gratuita que estima tu ahorro potencial en energía solar a partir de los datos que proporcionas. Los resultados son estimativos y no constituyen un presupuesto vinculante." },
        { h: "2. Sin compromiso", p: "El uso de la calculadora y la recepción de un informe o presupuesto no implican ninguna obligación. Puedes detenerte en cualquier momento y solicitar la eliminación de tus datos." },
        { h: "3. Precisión", p: "Nuestros cálculos se basan en datos medios del mercado español. El ahorro real puede variar según las circunstancias de tu vivienda, cambios de tarifa o subvenciones regionales." },
        { h: "4. Responsabilidad", p: "Recywatt Iberica S.L. no se responsabiliza de las decisiones tomadas con base en la calculadora. Para una instalación recomendamos siempre un instalador certificado." },
        { h: "5. Propiedad intelectual", p: "Todo el contenido, software y elementos visuales de DoctorSol son propiedad de Recywatt Iberica S.L. y no pueden copiarse sin autorización escrita." },
        { h: "6. Ley aplicable", p: "Estos términos se rigen por la legislación española. Cualquier disputa se someterá a los tribunales competentes del domicilio social de Recywatt Iberica S.L." },
      ],
    },
  },
  en: {
    nav_back: "Back to home", nav_calc: "Go to calculator",
    last_updated: "Last updated: February 2026",
    review_note: "Version pending legal review — processing in accordance with GDPR and Spanish LOPDGDD.",
    contact_h: "Questions?",
    contact_p: "For privacy or legal matters please email",
    privacy: {
      title: "Privacy Policy",
      sub: "How Recywatt Iberica S.L. handles your data.",
      blocks: [
        { h: "1. Who we are", p: "Data controller is Recywatt Iberica S.L., registered in Spain. We operate the DoctorSol platform via doctorsol.es and related subdomains." },
        { h: "2. What data we collect", p: "We process: name, email, phone/WhatsApp, location (city/postcode), electricity bill data (if uploaded), calculator answers, IP address and browser metadata." },
        { h: "3. Why we use it", p: "To run the calculation, generate your personal report or quote, send confirmation emails, prevent fraud and produce anonymised market analyses." },
        { h: "4. With whom we share", p: "Only with service providers needed to deliver our service (email, hosting, maps, payment processing). Never with third parties for marketing without your explicit consent." },
        { h: "5. How long we keep it", p: "Lead and intake data for 24 months after your last interaction. You may request earlier deletion via the contact below." },
        { h: "6. Your rights", p: "Right of access, rectification, erasure, restriction and portability. You may lodge a complaint with the Spanish authority AEPD (aepd.es)." },
        { h: "7. Cookies & analytics", p: "Essential cookies plus, with your consent, Meta Pixel and Google Analytics 4 to improve the calculator. You may refuse them at any time via your browser." },
      ],
    },
    terms: {
      title: "Terms & Conditions",
      sub: "Conditions for using DoctorSol.",
      blocks: [
        { h: "1. The service", p: "DoctorSol provides a free online calculator that estimates your potential solar savings from the data you provide. Results are estimates and do not constitute a binding quote." },
        { h: "2. No obligation", p: "Using the calculator and receiving a report or quote creates no obligation. You may stop at any time and request deletion of your data." },
        { h: "3. Accuracy", p: "Our calculations rely on average Spanish market data. Real-world savings can vary depending on your home, tariff changes or regional subsidies." },
        { h: "4. Liability", p: "Recywatt Iberica S.L. is not liable for decisions you make based on the calculator. For installation we always recommend a certified installer." },
        { h: "5. Intellectual property", p: "All content, software and visuals on DoctorSol belong to Recywatt Iberica S.L. and may not be copied without written permission." },
        { h: "6. Governing law", p: "These terms are governed by Spanish law. Disputes shall be submitted to the competent courts at the registered office of Recywatt Iberica S.L." },
      ],
    },
  },
};
const t = (lang) => STR[(lang || "es").split("-")[0].toLowerCase()] || STR.es;

export default function Legal({ type = "privacy" }) {
  const { i18n } = useTranslation();
  const T = t(i18n.language);
  const doc = type === "terms" ? T.terms : T.privacy;
  return (
    <div className="min-h-screen bg-[#F8F6F0] text-[#0F172A]"
         style={{ fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif", WebkitFontSmoothing: "antialiased", letterSpacing: "-0.01em" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@600;700;800&family=Inter:wght@400;500;600&display=swap');
        .h-display { font-family: 'Outfit', sans-serif; font-weight: 800; letter-spacing: -0.04em; line-height: 0.95; }
        .h-display-thin { font-family: 'Outfit', sans-serif; font-weight: 600; letter-spacing: -0.035em; }
      `}</style>

      <header className="px-5 md:px-12 lg:px-20 py-5 flex items-center justify-between border-b border-[#0F172A]/5">
        <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
          <div className="w-9 h-9 rounded-full bg-[#E78938] flex items-center justify-center"><Sun className="w-5 h-5 text-white" /></div>
          <span className="h-display-thin text-xl">DoctorSol</span>
        </Link>
        <div className="flex items-center gap-3">
          <LanguageSwitcher variant="minimal" />
          <Link to="/" className="text-sm text-[#475569] hover:text-[#0F172A] hidden sm:inline" data-testid="nav-back">{T.nav_back}</Link>
        </div>
      </header>

      <section className="px-5 md:px-12 lg:px-20 py-16 md:py-24">
        <article className="max-w-3xl mx-auto">
          <h1 className="h-display text-[44px] sm:text-[52px] lg:text-[64px] mb-3">{doc.title}</h1>
          <p className="text-lg text-[#475569] mb-3">{doc.sub}</p>
          <p className="text-base text-[#94A3B8] mb-12">{T.last_updated}</p>

          {doc.blocks.map((b) => (
            <div key={b.h} className="mb-9">
              <h2 className="h-display-thin text-xl md:text-2xl mb-3">{b.h}</h2>
              <p className="text-base text-[#334155] leading-relaxed">{b.p}</p>
            </div>
          ))}

          <div className="bg-[#0F172A] text-white rounded-2xl p-7 md:p-9 mt-14">
            <div className="text-base font-semibold text-[#E78938] mb-2">{T.contact_h}</div>
            <p className="text-base text-white/80 mb-3">{T.contact_p}</p>
            <a href="mailto:info@doctorsol.es" className="h-display-thin text-2xl text-[#E78938] hover:underline inline-flex items-center gap-2">
              <Mail className="w-5 h-5" /> info@doctorsol.es
            </a>
            <p className="text-sm text-white/40 mt-5 italic">{T.review_note}</p>
          </div>

          <div className="mt-12 text-center">
            <Link to="/" className="inline-flex items-center gap-2 bg-[#E78938] text-white rounded-full px-8 py-3.5 font-semibold text-base hover:bg-[#D17627] transition-colors">
              {T.nav_calc} →
            </Link>
          </div>
        </article>
      </section>

      <footer className="border-t border-[#0F172A]/10 px-5 md:px-12 lg:px-20 py-8 text-sm text-[#94A3B8] flex flex-wrap justify-between gap-3">
        <div>© 2026 Recywatt Iberica S.L. · DoctorSol</div>
        <div className="flex gap-5">
          <Link to="/over-ons">Over ons</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/voorwaarden">Voorwaarden</Link>
          <a href="mailto:info@doctorsol.es">Contact</a>
        </div>
      </footer>
    </div>
  );
}
